"use client";

import { memo, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { OptionBadge } from "@/components/ui/option-badge";

import { summarizeRule } from "../../lib/rule-summary";
import type { SelectionRule } from "../../lib/types";
import { FieldRow, SectionCard } from "../shared";

interface Props {
  value: SelectionRule | null;
  onChange: (rule: SelectionRule | null) => void;
  stages?: { id: string; name: string }[];
  /** Custom-property schema from spec 02; undefined in 01 → the predicate criterion renders disabled. */
  propertySchema?: undefined;
  disabled?: boolean;
  readOnly?: boolean;
}

const MAX_KEYWORDS = 10;
const MAX_KEYWORD_LEN = 60;

/**
 * Memoized because it sits next to the field editor in the sheet: every
 * keystroke in a field label re-renders the form, and this section's own props
 * don't change, so it would otherwise re-render its chips and selects for
 * nothing.
 */
export const SelectionRuleEditor = memo(function SelectionRuleEditor({ value, onChange, stages, disabled, readOnly }: Props) {
  const locked = Boolean(disabled || readOnly);
  const rule = value ?? {};
  const set = (patch: Partial<SelectionRule>) => {
    const next = { ...rule, ...patch };
    (Object.keys(next) as (keyof SelectionRule)[]).forEach((k) => {
      const v = next[k];
      if (v == null || (Array.isArray(v) && v.length === 0) || v === "") delete next[k];
    });
    onChange(Object.keys(next).length ? next : null);
  };

  return (
    <SectionCard iconName="IconTarget" title="Default selection rule">
      <p className="text-[13px] text-contrast/60">
        Picked automatically when <strong className="font-medium text-heading">all</strong> of the following hold.
        Interviewers can still choose by hand.
      </p>

      {stages && (
        <FieldRow label="Candidate stage">
          <div className="flex flex-wrap gap-2">
            {stages.map((s) => {
              const on = rule.stage_ids?.includes(s.id) ?? false;
              return (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-contrast/3 px-2 py-1 text-[13px] text-contrast/70"
                >
                  <Checkbox
                    aria-label={s.name}
                    checked={on}
                    disabled={locked}
                    onChange={() =>
                      set({
                        stage_ids: on
                          ? (rule.stage_ids ?? []).filter((x) => x !== s.id)
                          : [...(rule.stage_ids ?? []), s.id],
                      })
                    }
                  />
                  {s.name}
                </label>
              );
            })}
          </div>
        </FieldRow>
      )}

      <FieldRow
        label="Title contains any of"
        helper={readOnly ? undefined : "Press Enter or comma to add a keyword."}
      >
        <TitleKeywordsInput
          keywords={rule.title_keywords ?? []}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(title_keywords) => set({ title_keywords })}
        />
      </FieldRow>

      <FieldRow
        label="Custom property"
        htmlFor="sc-rule-property"
        helper="Available once conversation properties ship."
        disabled
      >
        <Input
          id="sc-rule-property"
          aria-label="Custom property"
          variant="readonly"
          size="sm"
          disabled
          value=""
          placeholder="Available once conversation properties ship"
        />
      </FieldRow>

      <FieldRow label="ATS interview-kit name" htmlFor="sc-rule-ats-kit">
        <Input
          id="sc-rule-ats-kit"
          aria-label="ATS interview-kit name"
          variant="modal"
          size="sm"
          placeholder="Technical Interview"
          disabled={locked}
          value={rule.ats_interview_kit_name ?? ""}
          onChange={(e) => set({ ats_interview_kit_name: e.target.value || undefined })}
        />
      </FieldRow>

      <div className="flex flex-col gap-0.5 rounded-md border border-border bg-contrast/2 px-3 py-2">
        <span className="text-xs font-medium text-contrast/60">Matches</span>
        <span className="text-[13px] text-contrast/70">
          {value ? summarizeRule(value, stages ?? []) : "Nothing yet — this type is only picked by hand."}
        </span>
      </div>
    </SectionCard>
  );
});

function TitleKeywordsInput({
  keywords,
  onChange,
  disabled,
  readOnly,
}: {
  keywords: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const kw = raw.replace(/,/g, "").trim().slice(0, MAX_KEYWORD_LEN);
    setDraft("");
    if (!kw || keywords.includes(kw) || keywords.length >= MAX_KEYWORDS) return;
    onChange([...keywords, kw]);
  };

  return (
    <div className="flex flex-col gap-2">
      {keywords.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {keywords.map((kw) => (
            <li key={kw} className="flex">
              <OptionBadge.Container variant="outline" size="xs">
                <OptionBadge.Name>{kw}</OptionBadge.Name>
                {!readOnly && (
                  <OptionBadge.RemoveButton
                    aria-label={`Remove keyword ${kw}`}
                    onClick={() => {
                      if (disabled) return;
                      onChange(keywords.filter((x) => x !== kw));
                    }}
                  />
                )}
              </OptionBadge.Container>
            </li>
          ))}
        </ul>
      )}
      {!readOnly && (
        <Input
          aria-label="Add title keyword"
          variant="modal"
          size="sm"
          placeholder="technical, system design"
          disabled={disabled}
          maxLength={MAX_KEYWORD_LEN}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit(draft);
              return;
            }
            if (e.key === "Backspace" && draft === "" && keywords.length > 0) {
              onChange(keywords.slice(0, -1));
            }
          }}
        />
      )}
    </div>
  );
}
